<template>
    <main class="layout__main layout__main--no-top-padding">
        <template v-if="!error && guideline">
            <template v-if="pending">
                <BaseGuidelineHeader>
                    <content-placeholders>
                        <content-placeholders-heading />
                    </content-placeholders>

                    <template #navigation>
                        <content-placeholders-img
                            v-for="i in [1,2,3]"
                            :key="i"
                        />
                    </template>
                </BaseGuidelineHeader>
            </template>
            <template v-else>
                <BaseGuidelineHeader :guideline-slug="route.params.slug">
                    {{ guideline?.title }}

                    <template
                        v-if="guideline"
                        #navigation
                    >
                        <BaseGuidelineDownload
                            v-if="guideline.guidelineFile && guideline.guidelineFile.length"
                            :url="guideline.guidelineFile[0].url"
                        >
                            <template #default>
                                Want to read the guideline in your own time? Download the PDF
                            </template>
                            <template #name>Download full guideline</template>
                        </BaseGuidelineDownload>

                        <BaseGuidelineDownload
                            v-if="guideline.pocketGuidelineFile && guideline.pocketGuidelineFile.length"
                            :url="guideline.pocketGuidelineFile[0].url"
                        >
                            <template #default>
                                Looking for a quick overview? Check the pocket guidelines.
                            </template>
                            <template #name>Download pocket guidelines</template>
                        </BaseGuidelineDownload>

                        <BaseGuidelineDownload
                            v-if="guideline.guidelineForPatients && guideline.guidelineForPatients.length"
                            :url="guideline.guidelineForPatients[0].url"
                        >
                            <template #default>
                                Are you a patient? Check the guidelines written for patients.
                            </template>
                            <template #name>Guidelines for patients</template>
                        </BaseGuidelineDownload>
                    </template>
                </BaseGuidelineHeader>

                <BaseGuidelineNavigation
                    :show-related-content="guideline?.showRelatedContent"
                    :guideline-slug="route.params.slug"
                />
            </template>
            <!-- Show either a child route or the first chapter of the guidelines -->
            <template v-if="route.name === 'guideline-slug' || route.name === 'guideline-chapterSlug'">
                <BaseGuidelineSearch
                    v-if="guideline"
                    :guideline="guideline"
                    :pending="pending"
                />

                <TheGuidelinesLayout>
                    <template #sidebar>
                        <BaseGuidelineChapters
                            :chapters="guideline ? guideline.chapters : null"
                            :pending="pending"
                        />
                    </template>

                    <template #default>
                        <BaseStickyBar data-full-width>
                            <BaseGuidelineChaptersMobile
                                :chapters="guideline ? guideline.chapters : null"
                                :pending="pending"
                            />
                        </BaseStickyBar>

                        <TheGuidelinesPagesChapter
                            :guideline="guideline"
                            :pending="pending"
                        />
                        <BaseScrollToTop />
                    </template>
                </TheGuidelinesLayout>
            </template>

            <BaseGuidelineSearch
                v-if="route.name === 'related-content'"
                :guideline="guideline"
                :pending="pending"
            />

            <NuxtPage
                v-if="route.name !== 'guideline-chapterSlug'"
                :guideline="guideline"
                :page-key="route.path"
                :show-related-content="guideline.showRelatedContent"
            />

            <TheChatbotPopover
                v-if="bot"
                :bot="Array.isArray(bot) ? bot[0] : bot"
            />
        </template>

        <BaseError
            v-else
            :error="error || { statusCode: 404 }"
        />
    </main>
</template>

<script setup>
import TheChatbotPopover from '~/components/TheChatbotPopover/TheChatbotPopover.vue';
import { useGuidelineSearchStore } from '~/store/guideline-search';

const { activeSite } = useMultisite();
import GuidelineQuery from '~/graphql/queries/guidelines/single.graphql';

const route = useRoute();
const guidelineSearchStore = useGuidelineSearchStore();

const { variables } = useCraftGraphql();

if (activeSite.value?.handle) {
    variables.site = activeSite.value?.handle;
}

const { data: entry, error, pending } = await useLazyAsyncQuery({
    query: GuidelineQuery,
    variables: {
        ...variables,
        slug: route.params.slug
    }
});

const bot = computed(() => toValue(entry)?.guideline?.chatBotSelector);

const guideline = computed(() => {
    if (!entry.value?.guideline) {
        return [];
    }

    return entry.value?.guideline;
});

// Parse subheadings for all chapters when guideline loads
watch(guideline, (newGuideline) => {
    if (newGuideline?.chapters?.length) {
        newGuideline.chapters.forEach((chapter) => {
            if (chapter.slug && chapter.chapterContent) {
                guidelineSearchStore.parseAndSetSubheadingsFromHtml(chapter.slug, chapter.chapterContent);
            }
        });
    }
}, { immediate: true });

useEtherSeo(guideline, {}, activeSite);
</script>
